import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import chatUserMemoryService, {
  type ChatUserPreference,
} from '../../services/api/chatUserMemoryService';
import {
  setProactiveGreetingEnabled,
  setCloseChatPanelOnNavigation,
} from '../slices/chatPreferencesSlice';

/**
 * Spec F (chat-proactive-greeting), Phase 10 — async thunks for the in-chat
 * preferences popover. Read/write the two Spec F preference keys through the
 * existing Spec C `chatUserMemoryService` (strict-bucket tenant isolation is
 * handled server-side).
 *
 * Design.md "Spec C memory key contract":
 *   chat.preferences.proactiveGreetingEnabled      bool, default true
 *   chat.preferences.closeChatPanelOnNavigation    bool, default true
 *
 * Spec C stores all values as `string prefValue` + `string valueType` pairs.
 * We encode booleans as `"true"` / `"false"` with `valueType = "boolean"`
 * to match the `SysChatUserPreference.ValueTypeOptions` enum on the backend.
 */

const KEY_PROACTIVE_GREETING = 'chat.preferences.proactiveGreetingEnabled';
const KEY_CLOSE_ON_NAV = 'chat.preferences.closeChatPanelOnNavigation';
const VALUE_TYPE_BOOLEAN = 'boolean';

type ChatPreferenceKey = 'proactiveGreetingEnabled' | 'closeChatPanelOnNavigation';

/**
 * Read the active tenant id from Redux state. A null active tenant is a
 * valid distinct memory scope — the service layer forwards it to the backend
 * unchanged (see `chatUserMemoryThunks.getActiveTenantId`).
 */
function getActiveTenantId(state: RootState): number | null {
  const raw = state.tenant.activeTenant?.tenantId ?? null;
  return raw != null ? Number(raw) : null;
}

/**
 * Parse a stored preference value into a boolean. The backend stores bools
 * as the literal strings `"true"` / `"false"`; anything else is treated as
 * the default (`true` per Requirement 14.5).
 */
function parseBool(pref: ChatUserPreference | undefined, fallback: boolean): boolean {
  if (!pref) return fallback;
  const v = (pref.prefValue ?? '').trim().toLowerCase();
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

function extractMessage(error: unknown, fallback: string): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e: any = error;
  const data = e?.originalError?.response?.data ?? e?.response?.data ?? null;
  if (data?.message) return data.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

// ============================================================================
// loadChatPreferences — reads both keys from Spec C user memory
// ============================================================================

export const loadChatPreferences = createAsyncThunk<
  { proactiveGreetingEnabled: boolean; closeChatPanelOnNavigation: boolean },
  void,
  { state: RootState; rejectValue: string }
>(
  'chatPreferences/load',
  async (_arg, { getState, rejectWithValue }) => {
    try {
      const tenantId = getActiveTenantId(getState());
      // Spec C exposes a list-all endpoint; filter locally to the two Spec F
      // keys. This is one round-trip instead of two and matches how the
      // inspector component already consumes preferences.
      const items = await chatUserMemoryService.listPreferences(tenantId);
      const byKey = new Map(items.map((p) => [p.prefKey, p]));
      return {
        proactiveGreetingEnabled: parseBool(byKey.get(KEY_PROACTIVE_GREETING), true),
        closeChatPanelOnNavigation: parseBool(byKey.get(KEY_CLOSE_ON_NAV), true),
      };
    } catch (error: unknown) {
      return rejectWithValue(extractMessage(error, 'Failed to load chat preferences'));
    }
  }
);

// ============================================================================
// updateChatPreference — optimistic local update + persist to Spec C memory
// ============================================================================

export const updateChatPreference = createAsyncThunk<
  void,
  { key: ChatPreferenceKey; value: boolean },
  { state: RootState; rejectValue: string }
>(
  'chatPreferences/update',
  async ({ key, value }, { dispatch, getState, rejectWithValue }) => {
    // 1. Capture the previous value for rollback before the optimistic swap.
    const prev = getState().chatPreferences[key];

    // 2. Optimistic local update.
    if (key === 'proactiveGreetingEnabled') {
      dispatch(setProactiveGreetingEnabled(value));
    } else {
      dispatch(setCloseChatPanelOnNavigation(value));
    }

    // 3. Persist via Spec C user-memory service.
    const memoryKey =
      key === 'proactiveGreetingEnabled' ? KEY_PROACTIVE_GREETING : KEY_CLOSE_ON_NAV;
    const tenantId = getActiveTenantId(getState());

    try {
      await chatUserMemoryService.upsertPreference(
        memoryKey,
        value ? 'true' : 'false',
        VALUE_TYPE_BOOLEAN,
        tenantId
      );
    } catch (error: unknown) {
      // 4. Roll back the optimistic update.
      if (key === 'proactiveGreetingEnabled') {
        dispatch(setProactiveGreetingEnabled(prev));
      } else {
        dispatch(setCloseChatPanelOnNavigation(prev));
      }
      return rejectWithValue(extractMessage(error, 'Failed to save chat preference'));
    }
  }
);
